import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useClaudeToolsStale } from './hooks/useClaudeToolsStale';
import WhatsNewPanel, { useWhatsNew, Update } from './components/WhatsNewPanel';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { IS_RECMAPPER, BRAND, RECMAPPER_NAV } from './product';
import GlobalSearch from './GlobalSearch';
import { requestLeave } from './navGuard';
import { opensNewTab } from './components/AppLink';

interface SidebarProps {
  collapsed: boolean;
  onToggleCollapse: () => void;
  // Phones (< 768px): the sidebar is a drawer, opened from the top bar's ☰.
  mobileOpen?: boolean;
  onMobileClose?: () => void;
}

interface NavItem {
  key: string;
  label: string;
  icon: React.ReactNode;
  path: string;
  badge?: number;
  /** A blue "i": something here needs the user (tooltip text). */
  alert?: string;
  tag?: string;                    // small text chip, e.g. "Soon"
  // section on a child starts a new sub-group: a mini break + tiny label
  children?: { key: string; label: string; path: string; section?: string }[];
  adminOnly?: boolean;
  accent?: keyof typeof ACCENTS;
}

// Section colour coding: each product area gets its own accent, applied as a
// translucent tint on the active tab. Dashboard/Projects = blue (core),
// Search = violet (dataset), ATS/CRM = emerald (records/money),
// Campaigns = amber (outreach). Tools stay neutral blue.
const ACCENTS = {
  blue: { active: 'bg-blue-500/10 text-blue-700 ring-1 ring-blue-200/70', icon: 'text-blue-600', child: 'text-blue-700 bg-blue-500/10' },
  violet: { active: 'bg-violet-500/10 text-violet-700 ring-1 ring-violet-200/70', icon: 'text-violet-600', child: 'text-violet-700 bg-violet-500/10' },
  emerald: { active: 'bg-emerald-500/10 text-emerald-700 ring-1 ring-emerald-200/70', icon: 'text-emerald-600', child: 'text-emerald-700 bg-emerald-500/10' },
  amber: { active: 'bg-amber-500/10 text-amber-700 ring-1 ring-amber-200/70', icon: 'text-amber-600', child: 'text-amber-700 bg-amber-500/10' },
} as const;

const Sidebar: React.FC<SidebarProps> = ({ collapsed, onToggleCollapse, mobileOpen = false, onMobileClose }) => {
  const { user, selectedProject, logout, stopImpersonating, token, team } = useAuth();
  const claudeToolsStale = useClaudeToolsStale(token);
  // What's new: the megaphone's unread count and the slide-over panel. The
  // panel shows what was unread when it opened, then everything is marked read.
  const whatsNew = useWhatsNew(IS_RECMAPPER ? null : token);
  const [whatsNewOpen, setWhatsNewOpen] = useState(false);
  const [whatsNewList, setWhatsNewList] = useState<Update[] | null>(null);
  const openWhatsNew = () => {
    setWhatsNewList(whatsNew.updates);
    setWhatsNewOpen(true);
    if (whatsNew.unread) whatsNew.markAllRead();
  };
  const navigate = useNavigate();
  const location = useLocation();
  const [expandedSection, setExpandedSection] = useState<string | null>(null);
  const [searchOpen, setSearchOpen] = useState(false);

  // ⌘K / Ctrl+K opens the global search palette from anywhere.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setSearchOpen((v) => !v);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const isActive = (full: string) => {
    const path = full.split('?')[0];
    if (path === '/' && location.pathname === '/') return true;
    if (path !== '/' && location.pathname.startsWith(path)) return true;
    return false;
  };

  // Main navigation items
  const mainItems: NavItem[] = [
    {
      key: 'dashboard',
      label: 'Dashboard',
      path: '/',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
        </svg>
      ),
    },
    {
      key: 'projects',
      label: 'Projects',
      path: '/projects',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
        </svg>
      ),
    },
    // Search: the full candidates+companies search (the V2 Lab experience,
    // Sample|Universe toggle included) for user 1 (staged); "coming soon"
    // teaser for everyone else. /v2lab now redirects here. The slimmer
    // /hm-search surface is reached from a project's Companies tab.
    {
      key: 'search',
      accent: 'violet',
      label: 'Search',
      path: '/search',
      tag: user?.id === 1 || user?.universeAccess ? undefined : 'Soon',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
        </svg>
      ),
    } as NavItem,
    // Native ATS/CRM module — live for user 1, visible as 'Soon' for everyone
    // else (same pattern as Search).
    ...(user?.id !== 1 ? ([{
      key: 'ats',
      accent: 'emerald',
      label: 'ATS / CRM',
      path: '/ats-soon',
      tag: 'Soon',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
        </svg>
      ),
    }] as NavItem[]) : []),
    ...(user?.id === 1 ? ([{
      key: 'ats',
      accent: 'emerald',
      label: 'ATS / CRM',
      path: '/ats',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
        </svg>
      ),
      children: [
        { key: 'ats-overview', label: 'Overview', path: '/ats' },
        { key: 'ats-reports', label: 'Reports', path: '/ats/reports' },
        { key: 'ats-inbox', label: 'Inbox', path: '/ats/inbox', section: 'Workspace' },
        { key: 'ats-tasks', label: 'Tasks', path: '/ats/tasks' },
        { key: 'ats-notes', label: 'Notes', path: '/ats/notes' },
        { key: 'ats-calendar', label: 'Calendar', path: '/ats/calendar' },
        { key: 'ats-candidates', label: 'Candidates', path: '/ats/candidates', section: 'ATS' },
        { key: 'ats-roles', label: 'Roles', path: '/ats/roles' },
        { key: 'ats-placements', label: 'Placements', path: '/ats/placements' },
        { key: 'ats-clients', label: 'Clients', path: '/ats/clients', section: 'CRM' },
        { key: 'ats-contacts', label: 'Contacts', path: '/ats/contacts' },
        { key: 'ats-deals', label: 'Deals', path: '/ats/deals' },
      ],
    }] as NavItem[]) : []),
  ];

  const vincereItem: NavItem | null = user?.teamId && team?.vincereConnected ? {
    key: 'vincere',
    label: 'Vincere',
    path: '/vincere',
    icon: (
      <img src="https://recmapper.com/wp-content/uploads/2026/03/vincere-logo.jpeg" alt="Vincere" className="w-5 h-5 rounded object-cover" />
    ),
  } : null;

  // Campaigns sits in MAIN: outreach is its own layer, fed by projects or the ATS.
  const campaignsItem: NavItem = {
    key: 'campaigns',
      accent: 'amber',
    label: 'Campaigns',
    path: '/campaigns',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z" />
      </svg>
    ),
  };
  mainItems.push(campaignsItem);

  const toolItems: NavItem[] = [
    {
      key: 'enrichment',
      label: 'Data Enrichment',
      path: '/enrichment',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
        </svg>
      ),
    },
    // Map Data - only for user 1 (replaces Market Monitoring)
    ...(user?.id === 1 ? [{
      key: 'mapdata',
      label: 'Map Data',
      path: '/mapdata',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
        </svg>
      ),
    }, {
      // Prospect Pages — personalised, trackable market previews (admin)
      key: 'prospect-pages',
      label: 'Prospect Pages',
      path: '/prospect-pages',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M13.828 10.172a4 4 0 010 5.656l-4 4a4 4 0 01-5.656-5.656l1.1-1.1m9.556-3.9l1.1-1.1a4 4 0 015.656 5.656l-4 4a4 4 0 01-5.656 0" />
        </svg>
      ),
    }] : []),
  ];

  const bottomItems: NavItem[] = [
    {
      key: 'training',
      label: 'Learn',
      path: '/training',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
        </svg>
      ),
    },
    {
      key: 'team',
      label: 'Team',
      path: '/team',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
        </svg>
      ),
      children: user?.teamRole === 'admin' ? [
        { key: 'team-members', label: 'Team Members', path: '/team' },
        { key: 'audit-logs', label: 'Audit Logs', path: '/team/audit' },
      ] : undefined,
    },
    {
      key: 'pricing',
      label: 'Pricing',
      path: '/pricing',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
    },
    {
      key: 'settings',
      label: 'Settings',
      path: claudeToolsStale ? '/settings?tab=integrations' : '/settings',
      alert: claudeToolsStale ? 'New Claude tools: refresh your MarketMapr connector' : undefined,
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
      ),
    },
  ];

  // Admin item — only shown for admin users
  const adminItem: NavItem | null = user?.role === 'admin' ? {
    key: 'admin',
    label: 'Admin',
    path: '/admin',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
      </svg>
    ),
  } : null;

  const onboardingItem: NavItem | null = user?.role === 'admin' ? {
    key: 'onboarding',
    label: 'Onboarding',
    path: '/admin/onboarding',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
      </svg>
    ),
  } : null;

  // Sidebar items are real links: Cmd/Ctrl-click, middle-click and "Open
  // in new tab" work like any website. A plain click navigates in the app,
  // after asking a page with unsaved work (navGuard.ts) first.
  const go = (path: string) => requestLeave(() => navigate(path));

  const renderNavItem = (item: NavItem) => {
    const active = isActive(item.path);
    const hasChildren = item.children && item.children.length > 0;
    const isExpanded = expandedSection === item.key;
    const accent = ACCENTS[item.accent || 'blue'];

    return (
      <div key={item.key}>
        <a
          href={item.path}
          onClick={(e) => {
            if (opensNewTab(e)) return; // Cmd / Ctrl / Shift / middle click: the browser opens a new tab
            e.preventDefault();
            if (hasChildren && !collapsed) {
              setExpandedSection(isExpanded ? null : item.key);
              if (!active) go(item.path);
            } else {
              go(item.path);
            }
          }}
          className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 group relative ${
            active
              ? `${accent.active} shadow-sm`
              : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
          }`}
          title={collapsed ? item.label : undefined}
        >
          <span className={`flex-shrink-0 relative ${active ? accent.icon : 'text-gray-400 group-hover:text-gray-600'}`}>
            {item.icon}
            {item.alert && collapsed && (
              <span className="absolute -top-1 -right-1 w-2.5 h-2.5 rounded-full bg-blue-600 ring-2 ring-white" aria-label={item.alert} />
            )}
          </span>
          {!collapsed && (
            <>
              <span className="flex-1 text-left truncate">{item.label}</span>
              {item.alert && (
                <span title={item.alert} aria-label={item.alert}
                  className="w-4 h-4 rounded-full bg-blue-600 text-white text-[10px] font-bold leading-none flex items-center justify-center flex-shrink-0 shadow-sm">i</span>
              )}
              {item.badge !== undefined && item.badge > 0 && (
                <span className="bg-blue-50 text-blue-600 text-[10px] font-semibold px-1.5 py-0.5 rounded-md ring-1 ring-inset ring-blue-600/10">
                  {item.badge >= 1000 ? `${(item.badge / 1000).toFixed(1).replace(/\.0$/, '')}k` : item.badge}
                </span>
              )}
              {item.tag && (
                <span className="bg-violet-500/10 text-violet-600 text-[9px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded-md ring-1 ring-inset ring-violet-600/10">
                  {item.tag}
                </span>
              )}
              {hasChildren && (
                <svg className={`w-4 h-4 transition-transform ${isExpanded ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              )}
            </>
          )}
        </a>

        {/* Children */}
        {hasChildren && isExpanded && !collapsed && (
          <div className="ml-8 mt-1 space-y-1">
            {item.children!.map(child => (
              <React.Fragment key={child.key}>
                {child.section && (
                  <div className="flex items-center gap-2 pt-2 pb-0.5 px-3">
                    <span className="text-[9px] font-bold uppercase tracking-widest text-gray-300">{child.section}</span>
                    <span className="flex-1 h-px bg-gray-100" />
                  </div>
                )}
                <a
                  href={child.path}
                  onClick={(e) => { if (opensNewTab(e)) return; e.preventDefault(); go(child.path); }}
                  className={`block w-full text-left px-3 py-1.5 rounded-md text-sm transition-colors ${
                    location.pathname === child.path
                      ? accent.child
                      : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  {child.label}
                </a>
              </React.Fragment>
            ))}
          </div>
        )}
      </div>
    );
  };

  // A RecMapper build shows only its own items (and their own children).
  const productNav = (items: NavItem[]): NavItem[] => (IS_RECMAPPER
    ? items.filter((i) => RECMAPPER_NAV.has(i.key)).map((i) => (i.children
      ? { ...i, children: i.children.filter((c) => RECMAPPER_NAV.has(c.key)) } : i))
    : items);

  const renderSection = (label: string, items: NavItem[]) => (
    <div className="mb-2">
      {!collapsed && (
        <div className="px-3 py-2">
          <span className="text-[10px] font-semibold uppercase tracking-wider text-gray-400">{label}</span>
        </div>
      )}
      {collapsed && <div className="border-t border-gray-200 mx-3 my-2" />}
      <div className="space-y-0.5">
        {items.map(renderNavItem)}
      </div>
    </div>
  );

  // Get user initials for avatar
  const getUserInitials = () => {
    if (user?.firstName && user?.lastName) {
      return `${user.firstName[0]}${user.lastName[0]}`.toUpperCase();
    }
    const email = user?.email || '';
    return email.slice(0, 2).toUpperCase();
  };

  const getUserName = () => {
    if (user?.firstName) return user.firstName;
    const email = user?.email || '';
    return email.split('@')[0];
  };

  return (
    <>
    <aside
      className={`fixed left-0 top-0 h-full bg-white/95 backdrop-blur-xl border-r border-gray-200/60 flex flex-col z-40 transition-all duration-250 shadow-sidebar ${
        collapsed ? 'w-16' : 'w-60'
      } max-md:w-72 max-md:z-[70] max-md:bg-white ${mobileOpen ? 'max-md:translate-x-0 max-md:shadow-2xl' : 'max-md:-translate-x-full'}`}
    >
      {/* Logo & Collapse Toggle */}
      <div className={`flex items-center h-16 border-b border-gray-100 ${collapsed ? 'justify-center px-2' : 'px-4 justify-between'}`}>
        {!collapsed ? (
          <div className="flex items-center gap-2.5 min-w-0">
            {user?.teamLogoUrl ? (
              <img
                src={user.teamLogoUrl}
                alt="Logo"
                className="h-8 max-w-[140px] object-contain"
              />
            ) : (
              <>
                <img src={BRAND.icon} alt={BRAND.name} className="w-8 h-8 rounded-lg flex-shrink-0" />
                <span className="text-lg font-bold bg-gradient-to-r from-[#0a2e6b] to-[#2196F3] bg-clip-text text-transparent truncate">
                  {BRAND.name}
                </span>
              </>
            )}
          </div>
        ) : (
          <img src={BRAND.icon} alt={BRAND.name} className="w-8 h-8 rounded-lg" />
        )}
        {!collapsed && onMobileClose && (
          <button
            onClick={onMobileClose}
            className="md:hidden p-2 rounded-lg text-gray-500 hover:text-gray-700 hover:bg-gray-100"
            title="Close menu"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
        {!collapsed && (
          <button
            onClick={onToggleCollapse}
            className="max-md:hidden p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-all duration-200"
            title="Collapse sidebar"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
            </svg>
          </button>
        )}
      </div>

      {/* Expand button when collapsed */}
      {collapsed && (
        <button
          onClick={onToggleCollapse}
          className="mx-auto mt-2 p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-all duration-200"
          title="Expand sidebar"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 5l7 7-7 7M5 5l7 7-7 7" />
          </svg>
        </button>
      )}

      {/* Global search trigger */}
      <div className="px-2 pt-3">
        <button
          onClick={() => setSearchOpen(true)}
          className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm text-gray-400 border border-gray-200/80 hover:border-gray-300 hover:text-gray-500 transition-colors ${collapsed ? 'justify-center' : ''}`}
          title="Search (⌘K)"
        >
          <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          {!collapsed && (
            <>
              <span className="flex-1 text-left">Search…</span>
              <kbd className="text-[10px] text-gray-400 border border-gray-200 rounded px-1 py-0.5">⌘K</kbd>
            </>
          )}
        </button>
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto px-2 py-3">
        {renderSection('Main', productNav(mainItems))}
        {productNav(vincereItem ? [vincereItem, ...toolItems] : toolItems).length > 0
          && renderSection('Tools', productNav(vincereItem ? [vincereItem, ...toolItems] : toolItems))}
      </nav>

      {/* Bottom Section */}
      <div className="border-t border-gray-100 px-2 py-3">
        <div className="space-y-0.5">
          {!IS_RECMAPPER && (
            <button
              onClick={openWhatsNew}
              className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-50 hover:text-gray-900 transition-all duration-200 group"
              title={collapsed ? (whatsNew.unread ? `What's new · ${whatsNew.unread} unread` : "What's new") : undefined}
            >
              <span className="flex-shrink-0 relative text-gray-400 group-hover:text-gray-600">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z" />
                </svg>
                {whatsNew.unread > 0 && collapsed && (
                  <span className="absolute -top-1 -right-1 w-2.5 h-2.5 rounded-full bg-blue-600 ring-2 ring-white" />
                )}
              </span>
              {!collapsed && (
                <>
                  <span className="flex-1 text-left truncate">What’s new</span>
                  {whatsNew.unread > 0 && (
                    <span className="min-w-[20px] h-5 px-1.5 rounded-full bg-blue-600 text-white text-[11px] font-semibold flex items-center justify-center tabular-nums">
                      {whatsNew.unread > 9 ? '9+' : whatsNew.unread}
                    </span>
                  )}
                </>
              )}
            </button>
          )}
          {productNav(bottomItems).map(renderNavItem)}
          {onboardingItem && !IS_RECMAPPER && renderNavItem(onboardingItem)}
          {adminItem && renderNavItem(adminItem)}
        </div>

        {/* User Profile & Logout */}
        <div className={`mt-3 pt-3 border-t border-gray-100 ${collapsed ? 'flex flex-col items-center gap-2' : 'flex items-center gap-3'}`}>
          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#2145b9] to-[#2f7cf2] flex items-center justify-center text-white font-semibold text-xs flex-shrink-0">
            {getUserInitials()}
          </div>
          {!collapsed && (
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-700 truncate">{getUserName()}</p>
            </div>
          )}
          {user?.impersonator && (
            <button
              onClick={async () => {
                try { await stopImpersonating(); }
                catch (e: any) { alert(e?.message || 'Failed to return to your account'); }
              }}
              className="p-1.5 rounded-md text-amber-600 hover:text-amber-700 hover:bg-amber-50 flex-shrink-0"
              title={`Return to your admin account (${user.impersonator.email})`}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a4 4 0 010 8H9m-6-8l4-4m-4 4l4 4" />
              </svg>
            </button>
          )}
          <button
            onClick={logout}
            className="p-1.5 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 flex-shrink-0"
            title="Sign out"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
            </svg>
          </button>
        </div>
      </div>

      <GlobalSearch open={searchOpen} onClose={() => setSearchOpen(false)} />
    </aside>
    {!IS_RECMAPPER && createPortal(
      <WhatsNewPanel open={whatsNewOpen} onClose={() => setWhatsNewOpen(false)} updates={whatsNewList ?? whatsNew.updates} />,
      document.body,
    )}
    </>
  );
};

export default Sidebar;
